import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { Input } from "../../ui/input";
import { Search } from "lucide-react";
import { API } from "../../../App";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const PartNameAutocomplete = ({ value, onChange, onSelectItem }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const portalTargetRef = useRef(null);

  const getPortalTarget = useCallback(() => {
    if (portalTargetRef.current) return portalTargetRef.current;
    if (!inputRef.current) return document.body;
    // Find the closest Radix Dialog content element
    const dialog = inputRef.current.closest('[role="dialog"]');
    portalTargetRef.current = dialog || document.body;
    return portalTargetRef.current;
  }, []);

  const calcPosition = useCallback(() => {
    if (!inputRef.current) return;
    const inputRect = inputRef.current.getBoundingClientRect();
    const container = getPortalTarget();
    const containerRect = container.getBoundingClientRect();

    // Position relative to the portal target container
    const left = inputRect.left - containerRect.left;
    const topBelow = inputRect.bottom - containerRect.top + 4;
    const bottomAbove = containerRect.bottom - inputRect.top + 4;
    const spaceBelow = containerRect.bottom - inputRect.bottom;
    const openAbove = spaceBelow < 200;

    setDropdownStyle({
      position: "absolute",
      left,
      width: inputRect.width,
      zIndex: 99999,
      ...(openAbove
        ? { bottom: bottomAbove, top: "auto" }
        : { top: topBelow, bottom: "auto" }),
    });
  }, [getPortalTarget]);

  // Recalculate position on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => calcPosition();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, calcPosition]);

  const searchInventory = (q) => {
    clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/inventory/items/search?q=${encodeURIComponent(q)}`, getAuthConfig());
        setSuggestions(res.data || []);
        if (res.data?.length > 0) { calcPosition(); setOpen(true); }
        else setOpen(false);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    searchInventory(e.target.value);
  };

  const handleSelect = (item) => {
    onSelectItem(item);
    setOpen(false);
    setSuggestions([]);
  };

  // Close on blur with delay to allow click on suggestion
  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  const dropdown = open && suggestions.length > 0 ? ReactDOM.createPortal(
    <div
      style={dropdownStyle}
      className="bg-popover border border-border rounded-lg shadow-xl max-h-48 overflow-y-auto overscroll-contain"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      data-testid="part-suggestions"
    >
      {suggestions.map((item) => (
        <button
          key={item.item_id}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSelect(item)}
          className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-2 text-sm transition-colors"
          data-testid={`suggestion-${item.item_id}`}
        >
          <div className="min-w-0">
            <span className="font-medium text-foreground block truncate">{item.name}</span>
            <span className="text-xs text-muted-foreground">{item.sku}{item.category ? ` · ${item.category}` : ""}</span>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-xs font-semibold text-foreground">${item.unit_price}</span>
            <span className="text-[10px] text-muted-foreground block">{item.stock_quantity} in stock</span>
          </div>
        </button>
      ))}
    </div>,
    getPortalTarget()
  ) : null;

  return (
    <div className="relative w-full sm:col-span-5">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
      <Input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) { calcPosition(); setOpen(true); } }}
        onBlur={handleBlur}
        className="input-glass pl-8"
        placeholder="Search inventory or type name..."
        data-testid="part-name-input"
      />
      {dropdown}
    </div>
  );
};

export default PartNameAutocomplete;
